import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Auth pages
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AuthCallback from '@/components/AuthCallback';
import SSOAuthorizePage from '@/pages/SSOAuthorizePage';
import SSOExamplePage from '@/pages/SSOExamplePage';

// Dashboard
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import DashboardOverview from '@/pages/dashboard/DashboardOverview';
import ProfilePage from '@/pages/dashboard/ProfilePage';
import SecurityPage from '@/pages/dashboard/SecurityPage';
import SessionsPage from '@/pages/dashboard/SessionsPage';
import ConnectedAppsPage from '@/pages/dashboard/ConnectedAppsPage';
import DeveloperPortalPage from '@/pages/dashboard/DeveloperPortalPage';

// Admin
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminOverview from '@/pages/admin/AdminOverview';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminAppsPage from '@/pages/admin/AdminAppsPage';
import AdminProvidersPage from '@/pages/admin/AdminProvidersPage';
import AdminAuditPage from '@/pages/admin/AdminAuditPage';

// Components
import ProtectedRoute from '@/components/ProtectedRoute';
import AdminRoute from '@/components/AdminRoute';

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Central SSO endpoints (used by other websites) */}
      <Route path="/sso/authorize" element={<SSOAuthorizePage />} />
      <Route path="/sso/demo" element={<SSOExamplePage />} />
      
      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardOverview />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="apps" element={<ConnectedAppsPage />} />
        <Route path="developer" element={<AdminRoute><DeveloperPortalPage /></AdminRoute>} />

        {/* Admin nested routes */}
        <Route path="admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminOverview />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="apps" element={<AdminAppsPage />} />
          <Route path="providers" element={<AdminProvidersPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
        </Route>
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" theme="dark" />
      </BrowserRouter>
    </div>
  );
}

export default App;
